<template>
  <div class="gleason-section">
    <div class="section-title" @click="toggleSection('summary')">
      <span>{{ $t('whole-slide-result') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.summary }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.summary" class="section-content">
      <table class="table is-fullwidth centered-table">
        <thead>
          <tr>
            <th>{{ $t('item') }}</th>
            <th>{{ $t('count') }}</th>
            <th>{{ $t('percentage') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in nottinghamSummaryRows">
            <tr :key="row.label">
              <td>
                <button class="algorithm-term-toggle" type="button" @click="toggleAlgorithmTerm(row.label)">
                  <span class="arrow" :class="{ open: isAlgorithmTermExpanded(row.label) }">▶</span>
                </button>
                <label class="switch is-small" @click.stop>
                  <input type="checkbox" :checked="isTermVisible(row.label)" @change="toggleTermVisibility(row.label, row.data)">
                  <span class="switch-slider"></span>
                </label>
                <span class="dot" :style="{ background: row.color }"></span>
                <span class="ml-2 term-label-cap">{{ row.displayLabel }}</span>
              </td>
              <td class="has-text-centered">{{ row.count }}</td>
              <td class="has-text-centered">{{ row.percentage }}</td>
            </tr>
            <tr v-if="isAlgorithmTermExpanded(row.label)" :key="`${row.label}-annotations`">
              <td colspan="3" class="algorithm-annotation-row">
                <algorithm-annotation-list
                  :state="algorithmTermState(row.label)"
                  :loading="isAlgorithmTermLoading(row.label)"
                  :color="row.colorHex"
                  :terms="allTerms"
                  :images="images"
                  @updateTermsOrTracks="$emit('updateTermsOrTracks', $event)"
                  @centerView="$emit('centerView', $event)"
                  @deletion="$emit('deletion', $event)"
                  @select="$emit('select', $event)"
                  @loadMore="requestAlgorithmAnnotations(row.label, true)"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div class="columns is-mobile is-multiline m-0">
        <div class="column is-4 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('tubules') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-primary">{{ result.scoring.tubulesScore }}</div>
          </div>
        </div>
        <div class="column is-4 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('pleomorphism') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-info">{{ result.scoring.pleomorphismScore }}</div>
          </div>
        </div>
        <div class="column is-4 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('mitosis') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-danger">{{ result.scoring.mitosisScore }}</div>
          </div>
        </div>
        <div class="column is-12 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('total-score') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-warning">
              {{ result.scoring.tubulesScore }} + {{ result.scoring.pleomorphismScore }} + {{ result.scoring.mitosisScore }} = {{ result.scoring.totalScore }}
            </div>
          </div>
        </div>
        <div class="column is-12 p-1">
          <div class="box p-2 has-text-centered scoring-box">
            <div class="has-text-grey">{{ $t('overall-grade') }}</div>
            <div class="is-size-5 has-text-weight-bold has-text-danger">{{ result.scoring.grade }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="section-title" @click="toggleSection('report')">
      <span>{{ $t('report') }}</span>
      <span class="arrow" :class="{ open: !result.sectionCollapsed.report }">▶</span>
    </div>
    <div v-show="!result.sectionCollapsed.report" class="section-content report-form">
      <div class="field is-horizontal is-small mb-2 align-items-center" v-if="result.report['mitosis']">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0 term-label-cap">mitosis</label></div>
        <div class="field-body d-flex-row">
          <div class="control report-inline-control">
            {{ $t('count') }}:
            <input class="input is-small report-inline-input" type="number" step="1" v-model.number="result.report['mitosis'].count">
          </div>
        </div>
      </div>

      <div
        v-for="label in percentageReportLabels"
        :key="label"
        class="field is-horizontal is-small mb-2 align-items-center"
        v-if="result.report[label] && result.report[label].percentage !== undefined"
      >
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0 term-label-cap">{{ label }}</label></div>
        <div class="field-body d-flex-row">
          <div class="control report-inline-control">
            {{ $t('percentage') }}:
            <input class="input is-small report-inline-input" type="number" step="0.1" v-model.number="result.report[label].percentage">
          </div>
        </div>
      </div>

      <hr class="my-2 has-background-grey-dark" />

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('mitosis-score') }}</label></div>
        <div class="field-body">
          <div class="control">
            <input class="input is-small report-inline-input" type="text" :value="calculateMitosisScore()" readonly>
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('tubules-score') }}</label></div>
        <div class="field-body">
          <div class="control">
            <input class="input is-small report-inline-input" type="text" :value="calculateTubulesScore()" readonly>
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('pleomorphism-score') }}</label></div>
        <div class="field-body">
          <div class="control">
            <input class="input is-small report-inline-input" type="text" :value="calculatePleomorphismScore()" readonly>
          </div>
        </div>
      </div>

      <hr class="my-2 has-background-grey-dark" />

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('total-score') }}</label></div>
        <div class="field-body">
          <div class="control">
            <input class="input is-small report-inline-input report-inline-input-bold" type="text" :value="calculateTotalScore()" readonly>
          </div>
        </div>
      </div>

      <div class="field is-horizontal is-small mb-2 align-items-center">
        <div class="field-label is-small is-normal report-label-wide"><label class="label mb-0">{{ $t('grade') }}</label></div>
        <div class="field-body">
          <div class="control">
            <input class="input is-small report-inline-input report-inline-input-bold" type="text" :value="calculateGrade()" readonly>
          </div>
        </div>
      </div>

      <button class="button is-primary is-small is-fullwidth mt-3">{{ $t('confirm-results') }}</button>
    </div>
  </div>
</template>

<script>
import AlgorithmAnnotationList from './AlgorithmAnnotationList';

export default {
  name: 'nottingham-result-panel',
  components: {
    AlgorithmAnnotationList
  },
  props: {
    result: {
      type: Object,
      required: true
    },
    project: Object,
    index: String,
    allTerms: {
      type: Array,
      default: () => []
    },
    ontologyTerms: {
      type: Object,
      default: () => ({})
    },
    images: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    percentageReportLabels() {
      return [
        'tubules',
        'invasive',
        'Pleomorphism Score 1 Nuclei',
        'Pleomorphism Score 2 Nuclei',
        'Pleomorphism Score 3 Nuclei'
      ];
    },
    nottinghamSummaryRows() {
      return [
        { label: 'invasive', displayLabel: this.$t('invasive'), data: this.result.rawData.invasive, count: '-', percentage: this.formatPercentage(this.result.rawData.invasive) },
        { label: 'mitosis', displayLabel: this.$t('mitosis'), data: this.result.rawData.mitosis, count: this.formatCount(this.result.rawData.mitosis), percentage: '-' },
        { label: 'tubules', displayLabel: this.$t('tubules'), data: this.result.rawData.tubules, count: '-', percentage: this.formatPercentage(this.result.rawData.tubules) },
        { label: 'Pleomorphism Score 1 Nuclei', displayLabel: this.$t('pleomorphism-score-1-nuclei'), data: this.result.rawData['Pleomorphism Score 1 Nuclei'], count: '-', percentage: this.formatPercentage(this.result.rawData['Pleomorphism Score 1 Nuclei']) },
        { label: 'Pleomorphism Score 2 Nuclei', displayLabel: this.$t('pleomorphism-score-2-nuclei'), data: this.result.rawData['Pleomorphism Score 2 Nuclei'], count: '-', percentage: this.formatPercentage(this.result.rawData['Pleomorphism Score 2 Nuclei']) },
        { label: 'Pleomorphism Score 3 Nuclei', displayLabel: this.$t('pleomorphism-score-3-nuclei'), data: this.result.rawData['Pleomorphism Score 3 Nuclei'], count: '-', percentage: this.formatPercentage(this.result.rawData['Pleomorphism Score 3 Nuclei']) }
      ]
        .filter(row => row.data && (row.data.count !== undefined || row.data.percentage !== undefined))
        .map(row => ({
          ...row,
          color: this.getTermColorByName(row.label) || '#9575CD',
          colorHex: this.algorithmTermColorHex(row.label, this.getTermColorByName(row.label) || '#9575CD')
        }));
    }
  },
  methods: {
    toggleSection(sectionName) {
      if (this.result.sectionCollapsed && typeof this.result.sectionCollapsed[sectionName] !== 'undefined') {
        this.result.sectionCollapsed[sectionName] = !this.result.sectionCollapsed[sectionName];
      }
    },
    ensureAlgorithmAnnotationState(termName) {
      if (!this.result.algorithmAnnotations) {
        this.$set(this.result, 'algorithmAnnotations', {});
      }
      if (!this.result.algorithmAnnotations[termName]) {
        this.$set(this.result.algorithmAnnotations, termName, {
          expanded: false,
          loading: false,
          error: false,
          items: [],
          page: 0,
          hasMore: false
        });
      }
      return this.result.algorithmAnnotations[termName];
    },
    algorithmTermState(termName) {
      return this.ensureAlgorithmAnnotationState(termName);
    },
    isAlgorithmTermExpanded(termName) {
      return this.algorithmTermState(termName).expanded;
    },
    isAlgorithmTermLoading(termName) {
      return this.algorithmTermState(termName).loading;
    },
    toggleAlgorithmTerm(termName) {
      const state = this.ensureAlgorithmAnnotationState(termName);
      state.expanded = !state.expanded;
      if (state.expanded && !state.items.length && !state.loading) {
        this.requestAlgorithmAnnotations(termName);
      }
    },
    requestAlgorithmAnnotations(termName, append = false) {
      this.$emit('loadAlgorithmAnnotations', { result: this.result, termName, append });
    },
    isTermVisible(termName) {
      const term = this.allTerms.find(t => t.name === termName);
      if (term && this.ontologyTerms[term.id]) {
        return this.ontologyTerms[term.id].visible;
      }
      return true;
    },
    toggleTermVisibility(termName, rawDataItem) {
      rawDataItem.showOnImage = !rawDataItem.showOnImage;
      this.$emit('toggleTermVisibility', termName);
    },
    getTermColorByName(name) {
      const term = this.allTerms.find(t => t.name === name);
      return term ? term.color : null;
    },
    algorithmTermColorHex(termName, fallbackColor = null) {
      return (fallbackColor || this.getTermColorByName(termName) || '#1E88E5').replace(/^#/, '');
    },
    formatCount(data) {
      return data && data.count !== undefined ? data.count : '-';
    },
    formatPercentage(data) {
      return data && data.percentage !== undefined ? `${data.percentage}%` : '-';
    },
    calculateTubulesScore() {
      const tubulesPercentage = this.result.report.tubules.percentage || 0;
      if (tubulesPercentage > 75) return 1;
      else if (tubulesPercentage >= 10) return 2;
      else return 3;
    },
    calculatePleomorphismScore() {
      const p1 = this.result.report['Pleomorphism Score 1 Nuclei'].percentage || 0;
      const p2 = this.result.report['Pleomorphism Score 2 Nuclei'].percentage || 0;
      const p3 = this.result.report['Pleomorphism Score 3 Nuclei'].percentage || 0;

      if (p3 > 0 && p3 >= p2 && p3 >= p1) return 3;
      if (p2 > 0 && p2 >= p1) return 2;
      return 1;
    },
    calculateMitosisScore() {
      const mitosisCount = this.result.report.mitosis.count || 0;
      if (mitosisCount < 9) return 1;
      else if (mitosisCount <= 17) return 2;
      else return 3;
    },
    calculateTotalScore() {
      return this.calculateTubulesScore() + this.calculatePleomorphismScore() + this.calculateMitosisScore();
    },
    calculateGrade() {
      const totalScore = this.calculateTotalScore();
      if (totalScore >= 8) return 'Grade 3';
      else if (totalScore >= 6) return 'Grade 2';
      else return 'Grade 1';
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/colors.scss';

.section-title {
  padding: 0.72rem 0.85rem;
  font-weight: 700;
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  background: rgba(222, 238, 255, 0.045);
  color: $dark-text-primary;
  align-items: center;
  margin-top: 8px;
  transition: background-color 0.16s ease;
}

.section-title:hover {
  background-color: rgba($primary, 0.08);
}

.section-title:first-child {
  margin-top: 0;
}

.section-content {
  padding: 0.8rem 0.85rem;
  background-color: rgba(8, 17, 29, 0.18);
  border: 1px solid rgba(190, 214, 238, 0.11);
  border-top: 0;
  border-radius: 0 0 8px 8px;
  font-size: 13px;
}

.section-content .table,
.section-content .label {
  font-size: 13px !important;
}

.section-content .input,
.section-content .select select {
  font-size: 13px !important;
  height: 2.25em;
}

.centered-table {
  background-color: transparent !important;
  color: $dark-text-primary !important;
}

.centered-table th {
  color: $dark-text-secondary !important;
  border-color: $dark-border-color !important;
  text-align: center !important;
}

.centered-table td {
  border-color: $dark-border-color !important;
  vertical-align: middle !important;
}

.centered-table tbody tr:hover {
  background-color: rgba($primary, 0.055);
}

.centered-table th:first-child,
.centered-table td:first-child {
  text-align: left !important;
}

.arrow {
  color: $dark-text-disabled;
  transition: transform 0.18s ease, color 0.18s ease;
}

.arrow.open {
  transform: rotate(90deg);
  color: $primary;
}

.switch {
  position: relative;
  width: 42px;
  height: 22px;
  display: inline-block;
}

.switch input {
  display: none;
}

.switch-slider {
  position: absolute;
  inset: 0;
  background: rgba(132, 156, 184, 0.16);
  border: 1px solid rgba(190, 214, 238, 0.15);
  border-radius: 20px;
  transition: 0.2s;
}

.switch-slider:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 1px;
  background: $dark-text-secondary;
  border-radius: 50%;
  transition: 0.2s;
}

input:checked+.switch-slider {
  background: $primary;
  border-color: $primary;
}

input:checked+.switch-slider:before {
  transform: translateX(19px);
}

.switch.is-small {
  width: 34px;
  height: 18px;
}

.switch.is-small .switch-slider:before {
  width: 14px;
  height: 14px;
}

.switch.is-small input:checked+.switch-slider:before {
  transform: translateX(16px);
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  display: inline-block;
  min-width: 12px;
  min-height: 12px;
}

.term-label-cap {
  text-transform: capitalize;
}

.algorithm-term-toggle {
  width: 1.4rem;
  height: 1.4rem;
  padding: 0;
  margin-right: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: $dark-text-secondary;
  cursor: pointer;
  vertical-align: middle;
}

.algorithm-term-toggle:hover {
  color: $dark-text-primary;
}

.algorithm-annotation-row {
  padding: 0 !important;
  background: rgba(8, 17, 29, 0.28);
}

.scoring-box {
  background: rgba(222, 238, 255, 0.038);
  border: 1px solid rgba(190, 214, 238, 0.12);
  border-radius: 8px;
}

.report-form .field-label {
  text-align: left;
  flex: 0 0 110px;
  max-width: 110px;
  padding-top: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.report-form .field-body {
  flex: 1 1 auto;
  min-width: 0;
  align-items: center !important;
  display: flex !important;
}

.report-form .control {
  display: flex !important;
  align-items: center !important;
  gap: 5px;
}

.report-form .field {
  margin-bottom: 0.65rem !important;
}

.report-form .field-label.report-label-wide {
  flex: 0 0 160px;
  max-width: 160px;
}

.report-form .control.report-inline-control {
  gap: 8px;
}

.report-form .input.report-inline-input {
  width: 88px;
  display: inline-block;
}

.report-form .input.report-inline-input-bold {
  font-weight: bold;
}

.align-items-center {
  align-items: center;
}

.d-flex-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}
</style>
